import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { PrinterConfigDto, UpsertPrinterConfigDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import { FormState } from 'final-form';
import { noop } from 'utils/functionUtils';
import { deletePrinter, findPrinterById, searchPrinters } from 'services/printerConfigServices';
import { debounce } from 'ts-debounce';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import PxGrid from 'components/layout/PxGrid';
import { Grid, Modal } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import AddButton from 'components/AddButton';
import EditActionButton from 'components/EditActionButton';
import DeleteActionButton from 'components/DeleteActionButton';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import UpsertPrinterConfigForm from 'components/settings/printer-config/UpsertPrinterConfigForm';
import { getAllPrinters } from 'services/printoutConfigServices';
import SearchResultComponent from 'components/SearchResultComponent';

interface PrinterConfSearchRequest {
  query: string;
}

const initialPrinterConfigSearch: Partial<PrinterConfSearchRequest> = {}

interface PrinterConfigFieldProps extends FormState {
  values: Partial<PrinterConfSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundPrinters: Array<PrinterConfigDto>,
  printersLoaded: boolean,
  addEditModalOpen: boolean,
  printer?: UpsertPrinterConfigDto,
  printerId?: number,
  printers: Array<PrinterConfigDto>,
  printersMap: Map<number, PrinterConfigDto>,
  searchValues: Partial<PrinterConfSearchRequest>,
  cancelTokenSource: CancelTokenSource
}

class PrinterConfigView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundPrinters: [],
      printersLoaded: false,
      addEditModalOpen: false,
      printer: undefined,
      printerId: undefined,
      printers: [],
      printersMap: new Map<number, PrinterConfigDto>(),
      searchValues: initialPrinterConfigSearch,
      cancelTokenSource
    }

    const onFinally = () => {
      this.setState({
        printersLoaded: false
      })
    };

    getAllPrinters(cancelTokenSource)
        .then(response => {

          let printersMap = new Map<number, PrinterConfigDto>();
          response.forEach(printer => {
            printersMap.set(printer.id, printer);
          })
          this.setState({
            printers: response,
            printersMap: printersMap
          })
        }).catch(noop)
        .finally(onFinally);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;
    if (id) {
      findPrinterById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop)
    }
  };

  showModal = (id?: number, printer?: UpsertPrinterConfigDto) => {

    this.setState(() => {
      return {
        addEditModalOpen: true,
        printer: printer,
        printerId: id
      }
    });
  };

  hideModal = () => {

    this.setState({
      addEditModalOpen: false,
      printer: undefined,
      printerId: undefined
    });
  }

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        printersLoaded: true
      })

    };

    const query = searchValues.query ? searchValues.query : "";

    searchPrinters(query, cancelTokenSource)
        .then(response => {
          this.setState({
            foundPrinters: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  }

  deletePrinter = (id: number): void => {
    const {cancelTokenSource} = this.state;

    deletePrinter(id, cancelTokenSource)
        .then(response => {
          this.setState(({foundPrinters}: State) => ({
            foundPrinters: foundPrinters.filter((printer: PrinterConfigDto) => printer.id != response.id)
          }))
        })
        .catch(noop);
  }

  handleChange = debounce(({values}: PrinterConfigFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({printersLoaded: false})

    const onFinally = () => {
      this.setState({
        printersLoaded: true
      })
    };

    const query = values.query ? values.query : "";

    searchPrinters(query, cancelTokenSource)
        .then(response => {
          this.setState({
            foundPrinters: response,
            searchValues: values
          })
        })
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {printersLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchPrinterSegment()}
          </div>
          <StyledViewTableRow>
            {!printersLoaded ?
                <LoaderComponent message={getMessage("printer.loading")}/> : this.renderPrinterConfigSegment()}
          </StyledViewTableRow>
          {this.renderUpsertPrinterConfigModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchPrinterSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundPrinters} = this.state;

    const numResults = foundPrinters.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("name")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="query"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Column width={7} verticalAlign="middle">
          </Grid.Column>
          <Grid.Column width={9}>
            {/*TODO SC: add is disabled because there are validations for fields that cannot be completed,
             will be enabled once the printer plugin is working*/}
            <AddButton disabled={true} buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
          </Grid.Column>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("printer.confirmDelete")}
              onConfirm={() => this.deletePrinter(cellData)}
          />
        </React.Fragment>
    );
  };

  rowGetter = ({index}: any) => {
    const {foundPrinters} = this.state;

    Object.assign(foundPrinters[index], {index: index + 1});

    return foundPrinters[index]
  };

  printersRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundPrinters} = this.state;

    const doctorId = foundPrinters[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(doctorId)}
        >
          {columns}
        </div>
    );
  };

  renderPrinterConfigSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundPrinters} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundPrinters.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.printersRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 500,
                label: (getMessage("name")),
                dataKey: "name"
              },
              {
                width: 900,
                label: (getMessage("printer.printerName")),
                dataKey: "printername"
              },
              {
                width: 400,
                label: (getMessage("printer.paperSize")),
                dataKey: "papersize"
              },
              {
                width: 500,
                label: (getMessage("printer.orientation")),
                dataKey: "orientation"
              },
              {
                width: 300,
                label: (getMessage("printer.marginX")),
                dataKey: "marginx"
              },
              {
                width: 300,
                label: (getMessage("printer.marginY")),
                dataKey: "marginy"
              },
              {
                width: 400,
                label: (getMessage("printer.firstOriginal")),
                dataKey: "firstorig"
              },
              {
                width: 400,
                label: (getMessage("printer.nextOriginal")),
                dataKey: "nextorig"
              },
              {
                width: 300,
                label: (getMessage("printer.firstCopy")),
                dataKey: "firstcopy"
              },
              {
                width: 300,
                label: (getMessage("printer.nextCopy")),
                dataKey: "nextcopy"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );

  };

  renderUpsertPrinterConfigModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, printer, printerId, printers} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("printer.cofiguration")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertPrinterConfigForm
                printer={printer}
                printers={printers}
                printerId={printerId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }
}

export default withMessages(PrinterConfigView);
